import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/common/PageHero';
import { CheckCircle2, ChevronRight } from 'lucide-react';

const Benefits = ({ items }) => (
  <ul className="space-y-3 text-gray-700">
    {items.map((b, i) => (
      <li key={i} className="flex items-start">
        <CheckCircle2 size={20} className="text-[#2c3e7d] mt-0.5 mr-3 flex-shrink-0" />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

const Section = ({ title, children }) => (
  <Card>
    <CardContent className="p-8">
      <h2 className="text-2xl font-bold text-[#2c3e7d] mb-2 uppercase">{title}</h2>
      <div className="w-20 h-1 bg-[#dc2626] mb-6" />
      <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
    </CardContent>
  </Card>
);

const Breadcrumbs = ({ trail }) => (
  <nav className="text-sm text-gray-600 mb-6" aria-label="Breadcrumb">
    <ol className="flex items-center flex-wrap gap-1">
      {trail.map((item, i) => (
        <li key={i} className="flex items-center">
          {item.to ? (
            <Link to={item.to} className="text-blue-600 hover:underline">{item.label}</Link>
          ) : (
            <span className="text-gray-700 font-medium">{item.label}</span>
          )}
          {i < trail.length - 1 && <ChevronRight size={14} className="mx-1 text-gray-400" />}
        </li>
      ))}
    </ol>
  </nav>
);

export const MembershipDoctors = () => (
  <div className="min-h-screen" data-testid="membership-doctors-page">
    <PageHero title="Membership" />
    <div className="container mx-auto px-4 py-12">
      <Breadcrumbs trail={[{ label: 'Home', to: '/' }, { label: 'Membership' }, { label: 'Doctors' }]} />
      <div className="space-y-8">
          <Section title="Doctor Membership">
            <p>
              Chirosport SA welcomes registered chiropractors with a special interest and qualification in
              sports chiropractic. Our doctor members are part of South Africa&rsquo;s national network
              of sports chiropractors and benefit from continuing education, professional development,
              event opportunities and patient referrals through the public directory.
            </p>
          </Section>

          <Section title="Member Benefits">
            <Benefits
              items={[
                'Listing in the public Find-a-Practitioner directory.',
                'Access to continuing education programmes and CPD-accredited events.',
                'Eligibility to attend / staff major sporting events with Chirosport SA.',
                'Access to FICS international resources and conferences.',
                'Regular news, research and clinical updates.',
                'Discounted access to selected partner products and services.',
              ]}
            />
          </Section>

          <Section title="Eligibility">
            <Benefits
              items={[
                'Current registration with the AHPCSA (Allied Health Professions Council of South Africa).',
                'Valid BHF (Board of Healthcare Funders) practice number.',
                'A demonstrable interest or qualification in sports chiropractic.',
                'Adherence to the Chirosport SA Code of Conduct.',
              ]}
            />
          </Section>

          <Section title="How to Join">
            <ol className="list-decimal list-inside space-y-2">
              <li>Complete the online <Link to="/register" className="text-blue-600 hover:underline">registration form</Link>.</li>
              <li>Upload your AHPCSA registration document.</li>
              <li>Verify your email with the one-time code.</li>
              <li>Wait for admin approval (usually within 2&ndash;3 working days).</li>
              <li>Pay the annual membership fee and access all member benefits.</li>
            </ol>
            <div className="pt-4">
              <Link
                to="/register"
                className="inline-block bg-[#2c3e7d] hover:bg-[#1e2a5a] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
                data-testid="doctors-join-btn"
              >
                Apply for Doctor Membership
              </Link>
            </div>
          </Section>
        </div>
    </div>
  </div>
);

export const MembershipStudents = () => (
  <div className="min-h-screen" data-testid="membership-students-page">
    <PageHero title="Membership" />
    <div className="container mx-auto px-4 py-12">
      <Breadcrumbs trail={[{ label: 'Home', to: '/' }, { label: 'Membership' }, { label: 'Students' }]} />
      <div className="space-y-8">
          <Section title="Student Membership">
            <p>
              Chirosport SA supports chiropractic students who are passionate about sports chiropractic.
              Student membership gives you early exposure to the field, mentorship from practising sports
              chiropractors and access to educational events at a reduced rate.
            </p>
          </Section>

          <Section title="Student Benefits">
            <Benefits
              items={[
                'Reduced rates on educational events and continuing education courses.',
                'Mentorship and shadowing opportunities with senior members.',
                'Networking at Chirosport SA and FICS events.',
                'Access to the member newsletter and learning resources.',
                'Pathway to full doctor membership upon graduation and AHPCSA registration.',
              ]}
            />
          </Section>

          <Section title="Eligibility">
            <Benefits
              items={[
                'Currently enrolled in an accredited chiropractic programme in South Africa.',
                'Valid student number from your tertiary institution.',
                'Genuine interest in sports chiropractic.',
              ]}
            />
          </Section>

          <Section title="How to Join">
            <ol className="list-decimal list-inside space-y-2">
              <li>Complete the online <Link to="/register" className="text-blue-600 hover:underline">registration form</Link> and select &ldquo;Student&rdquo;.</li>
              <li>Verify your email with the one-time code.</li>
              <li>Provide your Chirosport student number.</li>
              <li>Wait for admin approval and start accessing member benefits.</li>
            </ol>
            <div className="pt-4">
              <Link
                to="/register"
                className="inline-block bg-[#2c3e7d] hover:bg-[#1e2a5a] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
                data-testid="students-join-btn"
              >
                Apply for Student Membership
              </Link>
            </div>
          </Section>
        </div>
    </div>
  </div>
);

export default MembershipDoctors;
